/* The chat itself: the operator plays the Sandbox Fan on the right, the real AI
   answers on the left, and what the turn would have done outside the chat shows
   inline as muted event rows. */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Btn, Icon, InfoLine, NumInput } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { money, notificationText, type MediaMix, type Row } from './model';

/* Fan moves, written the way a fan would type them. */
const QUICK: Array<{ id: string; icon: string; text: string }> = [
  { id: 'pic', icon: 'image', text: 'can i see a pic of you?' },
  { id: 'haggle', icon: 'percent', text: 'that’s a bit expensive, can you do less?' },
  { id: 'broke', icon: 'credit-card', text: 'sorry i don’t have money rn' },
  { id: 'call', icon: 'phone', text: 'can we do a video call?' },
  { id: 'small', icon: 'message-circle', text: 'haha nothing much, just chilling. you?' },
];

export interface ChatPaneProps {
  name: string;
  /** typing: a turn is running; received: the fan's message waits for the AI to pick it up. */
  status: 'typing' | 'received' | 'online';
  rows: Row[];
  showCost: boolean;
  unlocking: Set<number>;
  deletable: boolean;
  onSend: (text: string) => Promise<boolean>;
  onTip: (amount: number, text: string) => Promise<boolean>;
  onUnlock: (messageId: number) => void;
  onDelete: () => void;
  /** Super admins only: open the trace of the turn that wrote a reply. */
  onTrace?: (turnId: string) => void;
  banner?: ReactNode;
}

export function ChatPane({ name, status, rows, showCost, unlocking, deletable, onSend, onTip, onUnlock, onDelete, onTrace, banner }: ChatPaneProps) {
  const { t } = useTranslation('assistantCompact');
  const [draft, setDraft] = useState('');
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState<number | null>(10);
  const [tipText, setTipText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const busy = status !== 'online';
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  // Follow the conversation unless the operator has scrolled up to read.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [rows.length, busy]);
  useEffect(() => { setConfirmDelete(false); }, [name]);

  const send = async (text: string) => {
    const v = text.trim();
    if (!v) return;
    stick.current = true;
    setDraft('');
    if (!(await onSend(v))) setDraft(v);
  };
  const tip = async () => {
    if (!tipAmount || tipAmount <= 0) return;
    stick.current = true;
    if (await onTip(tipAmount, tipText.trim())) { setTipOpen(false); setTipText(''); }
  };

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: C.surface, border: `1px solid ${C.borderLo}`, borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ height: 60, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px 0 16px', borderBottom: `1px solid ${C.divider}` }}>
        <div style={{ position: 'relative', width: 38, height: 38, flex: 'none' }}>
          <div style={{ width: 38, height: 38, borderRadius: 99, background: 'repeating-linear-gradient(135deg,var(--om-seg) 0 5px,var(--om-divider) 5px 10px)' }} />
          <span style={{ position: 'absolute', right: 0, bottom: 0, width: 10, height: 10, borderRadius: 99, background: C.purpleLt, border: `2px solid ${C.surface}` }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: '1 1 auto', minWidth: 90 }}>
          <span style={{ font: f(600, 14), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          <span style={{ font: f(400, 12), color: status === 'online' ? C.sub : C.purpleLt }}>{t(`sb.status.${status}`)}</span>
        </div>
        {confirmDelete ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
            <span style={{ font: f(500, 12), color: C.purplePale, whiteSpace: 'nowrap' }}>{t('sb.deleteConfirm')}</span>
            <Btn onClick={() => setConfirmDelete(false)} height={26}>{t('sb.deleteNo')}</Btn>
            <Btn kind="primary" icon="trash-2" onClick={onDelete} height={26}>{t('sb.deleteYes')}</Btn>
          </div>
        ) : (
          <>
            <span title={t('sb.testPill')} style={{ height: 24, minWidth: 0, maxWidth: '45%', flex: '0 1 auto', padding: '0 9px', display: 'flex', alignItems: 'center', gap: 5, background: C.tint16, borderRadius: 99, font: f(500, 11.5), color: C.purplePale, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              <Icon name="flask" size={12} style={{ flex: 'none' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('sb.testPill')}</span>
            </span>
            <button
              type="button"
              aria-label={t('sb.delete')}
              title={deletable ? t('sb.delete') : t('sb.deleteNotYours')}
              disabled={!deletable}
              onClick={() => setConfirmDelete(true)}
              className={deletable ? 'cpt-hover-icon' : undefined}
              style={{ width: 30, height: 30, flex: 'none', border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: deletable ? 'pointer' : 'not-allowed', opacity: deletable ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icon name="trash-2" size={15} />
            </button>
          </>
        )}
      </div>

      <div
        ref={scroller}
        onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}
        className="cpt-thin-scroll"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 18px 12px', display: 'flex', flexDirection: 'column' }}
      >
        {rows.map((r) => (
          r.t === 'sys' ? <SystemRow key={r.id} kind={r.kind} payload={r.payload} />
            : r.t === 'fan' ? <FanBubble key={r.id} text={r.text} tip={r.tip} tight={r.tight} pending={!!r.pending} />
              : <ModelBubble key={r.id} row={r} unlocking={typeof r.id === 'number' && unlocking.has(r.id)} onUnlock={onUnlock} onTrace={onTrace} />
        ))}
        {busy && (
          <div className="cpt-sb-in" style={{ alignSelf: 'flex-start', marginTop: 14 }}>
            <div style={{ display: 'flex', gap: 4, padding: '12px 14px', background: 'var(--om-divider)', borderRadius: '16px 16px 16px 4px' }}>
              {[0, 1, 2].map((i) => <span key={i} className="cpt-sb-dot" style={{ animationDelay: `${i * 0.15}s` }} />)}
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 'none', padding: '10px 14px 14px', display: 'flex', flexDirection: 'column', gap: 10, borderTop: `1px solid ${C.divider}` }}>
        {banner}
        {showCost && <InfoLine>{t('sb.costNote')}</InfoLine>}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {QUICK.map((q) => (
            <Chip key={q.id} icon={q.icon} title={q.text} onClick={() => void send(q.text)}>{t(`sb.quick.${q.id}`)}</Chip>
          ))}
          <Chip icon="dollar-sign" on={tipOpen} onClick={() => setTipOpen((o) => !o)}>{t('sb.quick.tip')}</Chip>
        </div>
        {tipOpen && (
          <div className="cpt-sb-in" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px 8px 12px', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, flexWrap: 'wrap' }}>
            <span style={{ font: f(500, 12.5), color: C.sub }}>{t('sb.tipAmount')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ font: f(500, 12.5), color: C.sub }}>$</span>
              <NumInput value={tipAmount} onChange={setTipAmount} min={1} max={10000} width={70} />
            </span>
            <input
              value={tipText}
              onChange={(e) => setTipText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void tip(); } }}
              placeholder={t('sb.tipNote')}
              style={{ flex: '1 1 160px', minWidth: 0, height: 28, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: f(400, 12.5), outline: 'none' }}
            />
            <Btn kind="primary" icon="dollar-sign" disabled={!tipAmount} onClick={() => void tip()}>{t('sb.sendTip')}</Btn>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 46, padding: '0 6px 0 16px', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 99 }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(draft); } }}
            placeholder={t('sb.placeholder')}
            aria-label={t('sb.placeholder')}
            style={{ flex: 1, minWidth: 0, background: 'transparent', border: 0, outline: 'none', color: C.text, font: f(400, 14) }}
          />
          <button type="button" onClick={() => void send(draft)} title={t('sb.send')} aria-label={t('sb.send')} disabled={!draft.trim()} style={{ width: 34, height: 34, flex: 'none', border: 0, borderRadius: 99, background: C.purple, color: '#fff', cursor: draft.trim() ? 'pointer' : 'default', opacity: draft.trim() ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="arrow-up" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Chip({ icon, title, on, onClick, children }: { icon: string; title?: string; on?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={title} className="cpt-sb-chip" style={{ height: 28, padding: '0 11px', display: 'flex', alignItems: 'center', gap: 6, background: on ? C.tint16 : C.card, border: `1px solid ${on ? C.purple : C.borderHi}`, borderRadius: 99, color: on ? C.text : 'var(--om-body)', font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <Icon name={icon} size={12} color={C.purpleLt} />
      {children}
    </button>
  );
}

function FanBubble({ text, tip, tight, pending }: { text: string; tip: number; tight: boolean; pending: boolean }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <div className="cpt-sb-in" style={{ alignSelf: 'flex-end', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginTop: tight ? 3 : 14, opacity: pending ? 0.6 : 1 }}>
      {tip > 0 && (
        <span style={{ height: 22, padding: '0 9px', display: 'flex', alignItems: 'center', gap: 4, background: C.tint20, borderRadius: 99, font: f(600, 11.5), color: C.purplePale }}>
          <Icon name="dollar-sign" size={11} />{t('sb.tipped', { amount: money(tip) })}
        </span>
      )}
      {text && (
        <div style={{ maxWidth: 400, padding: '10px 14px', background: C.purple, color: '#fff', borderRadius: '18px 18px 4px 18px', font: f(400, 14, 1.4), whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{text}</div>
      )}
    </div>
  );
}

function mixLabel(t: (k: string, o?: Record<string, unknown>) => string, mix: MediaMix) {
  return (['photo', 'video', 'audio'] as const).filter((k) => mix[k]).map((k) => t(`sb.mix.${k}`, { count: mix[k] })).join(' · ');
}

function ModelBubble({ row, unlocking, onUnlock, onTrace }: { row: Extract<Row, { t: 'model' }>; unlocking: boolean; onUnlock: (id: number) => void; onTrace?: (turnId: string) => void }) {
  const { t } = useTranslation('assistantCompact');
  const paid = row.price > 0;
  const hasCard = paid || !!row.mix;
  const unlockable = paid && !row.purchased && !row.deleted && typeof row.id === 'number';
  return (
    <div className="cpt-sb-in" style={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, marginTop: row.tight ? 3 : 14, opacity: row.deleted ? 0.55 : 1 }}>
      {row.text && (
        <div style={{ maxWidth: 400, padding: '10px 14px', background: 'var(--om-divider)', color: C.text, borderRadius: '18px 18px 18px 4px', font: f(400, 14, 1.4), whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', textDecoration: row.deleted ? 'line-through' : undefined }}>{row.text}</div>
      )}
      {hasCard && (
        <div style={{ width: 260, maxWidth: '100%', borderRadius: 16, overflow: 'hidden', background: C.card, border: `1px solid ${C.borderHi}` }}>
          <div style={{ height: 140, background: 'repeating-linear-gradient(135deg,var(--om-divider) 0 8px,var(--om-legacy-card) 8px 16px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: C.sub }}>
            <Icon name={!paid ? 'image' : row.purchased ? 'unlock' : 'lock'} size={22} />
            <span style={{ font: '500 11px var(--font-mono)' }}>{row.mix ? mixLabel(t, row.mix) : t('sb.paidMessage')}</span>
          </div>
          <div style={{ padding: 8 }}>
            {paid ? (
              <button
                type="button"
                disabled={!unlockable || unlocking}
                onClick={() => unlockable && onUnlock(row.id as number)}
                style={{ width: '100%', height: 36, border: 0, borderRadius: 10, background: unlockable ? C.purple : 'var(--om-border)', color: unlockable ? '#fff' : C.sub, font: f(600, 13), cursor: unlockable && !unlocking ? 'pointer' : 'default', opacity: unlocking ? 0.6 : 1 }}
              >
                {row.purchased ? t('sb.unlocked', { price: money(row.price) }) : t('sb.unlockFor', { price: money(row.price) })}
              </button>
            ) : (
              <div style={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', font: f(500, 12), color: C.muted }}>{t('sb.free')}</div>
            )}
          </div>
        </div>
      )}
      {row.deleted && <span style={{ font: f(400, 11.5), color: C.muted, paddingLeft: 4 }}>{t('sb.unsent')}</span>}
      {onTrace && row.endsTurn && row.turnId && (
        <button
          type="button"
          onClick={() => onTrace(row.turnId!)}
          title={t('sb.trace.open')}
          className="cpt-sb-chip"
          style={{ height: 20, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 4, background: C.tint16, border: '1px solid transparent', borderRadius: 99, font: f(500, 11), color: C.purplePale, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 4 }}
        >
          <Icon name="code" size={11} />
          {t('sb.trace.chip')}
        </button>
      )}
    </div>
  );
}

const SYS_ICON: Record<string, string> = { notification: 'bell', list_op: 'list', mark_unread: 'mail', fan_note: 'edit-3' };

function SystemRow({ kind, payload }: { kind: string; payload: Record<string, unknown> }) {
  const { t } = useTranslation('assistantCompact');
  const [open, setOpen] = useState(false);
  const note = kind === 'notification' ? notificationText(payload.message) : null;
  const text =
    kind === 'notification' ? `${t('sb.sys.notification')}${note?.head ? ` · ${note.head}` : ''}`
      : kind === 'list_op' ? t(payload.op === 'remove' ? 'sb.sys.listRemove' : 'sb.sys.listAdd', { list: String(payload.list ?? '') })
        : kind === 'mark_unread' ? t('sb.sys.markUnread')
          : kind === 'fan_note' ? t('sb.sys.fanNote', { note: String(payload.comment ?? '') })
            : t('sb.sys.other', { kind: kind || '?' });
  const more = !!note && note.body.length > 0;
  return (
    <div className="cpt-sb-in" style={{ alignSelf: 'center', margin: '14px 0 2px', maxWidth: '86%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        onClick={more ? () => setOpen((o) => !o) : undefined}
        title={text}
        style={{ maxWidth: '100%', minHeight: 26, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, background: 'color-mix(in srgb, var(--om-accent) 12%, transparent)', border: 0, borderRadius: 99, font: f(500, 11.5), color: C.purplePale, cursor: more ? 'pointer' : 'default' }}
      >
        <Icon name={SYS_ICON[kind] ?? 'info'} size={12} style={{ flex: 'none' }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        {more && <Icon name="chevron-down" size={12} style={{ flex: 'none', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />}
      </button>
      {open && note && (
        <div style={{ padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, font: f(400, 12, 1.5), color: C.sub, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {note.body.join('\n')}
        </div>
      )}
    </div>
  );
}
